import { IconButton } from '@mui/material';
import { FormProvider, useFormState } from 'react-hook-form';
import { DynamicIcon, Tooltip } from 'src/components';
import type { SelectMenuOption } from 'src/components/SelectMenu';
import type { BrandWithSupport } from 'src/graphql';
import { useCgCustomDomains } from 'src/graphql/entities/cg-custom-domain/use-cg-custom-domain';
import { PENDING_DOCUMENT_ID } from 'src/graphql/entities/legal-document/legal-document-cache';
import { LegalDocumentFields } from 'src/pages/Settings/memberships/components/legal-document/legal-document-fields';
import {
  type LegalDocumentSurface,
  NEW_DOCUMENT,
} from 'src/pages/Settings/memberships/hooks/use-legal-document-surface';
import {
  describeVersionSharing,
  describeVersionUsage,
} from 'src/pages/Settings/memberships/utils/describe-legal-document-version';
import {
  describeSharedDomainConflict,
  findSharedDomainConflict,
} from 'src/pages/Settings/memberships/utils/find-shared-domain-conflict';
import { useConfirmationModal } from 'src/utils/hooks';

interface LegalDocumentPanelProps {
  surface: LegalDocumentSurface;
  partnerId: number | null;
  selectedSegmentId: string;
  segments: Pick<BrandWithSupport, 'brandId' | 'name'>[];
}

export const LegalDocumentPanel = ({
  surface,
  partnerId,
  selectedSegmentId,
  segments,
}: LegalDocumentPanelProps) => {
  const { confirm } = useConfirmationModal();
  const { domains } = useCgCustomDomains(partnerId);
  const { isDirty } = useFormState({ control: surface.formMethods.control });
  const { savedDocument, documents } = surface;

  const segmentName = (brandId: string) =>
    segments.find((segment) => String(segment.brandId) === brandId)?.name ?? 'Unknown segment';

  const conflict = findSharedDomainConflict(domains, documents, selectedSegmentId);

  const confirmDiscard = (): Promise<boolean> =>
    isDirty
      ? confirm({
          title: 'Discard unsaved changes?',
          body: "Switching versions discards the changes you haven't saved.",
          confirmLabel: 'Discard changes',
          cancelLabel: 'Keep editing',
          danger: true,
        })
      : Promise.resolve(true);

  const handleSelect = async (documentId: string) => {
    const shouldSwitch = await confirmDiscard();

    if (shouldSwitch) await surface.selectDocument(documentId);
  };

  const handleNewVersion = async () => {
    const shouldSwitch = await confirmDiscard();

    if (shouldSwitch) surface.startNewVersion(segmentName(selectedSegmentId));
  };

  const handleDelete = async (documentId: string) => {
    const shouldDelete = await confirm({
      title: 'Delete this version?',
      body: "No segment uses this version. Deleting it can't be undone.",
      confirmLabel: 'Delete',
      cancelLabel: 'Keep it',
      danger: true,
    });

    if (shouldDelete) await surface.deleteDocument(documentId);
  };

  const versionOptions: SelectMenuOption[] = documents.map((document) => {
    const usage = describeVersionUsage(document, selectedSegmentId);
    const isDeletable =
      !document.brandIds.length && document.legalDocumentId !== PENDING_DOCUMENT_ID;

    return {
      value: document.legalDocumentId,
      title: document.name,
      description: (
        <span data-version-description="" title={usage}>
          {usage}
        </span>
      ),
      endIcon: isDeletable ? (
        <Tooltip title="Delete version">
          <IconButton
            size="small"
            data-version-delete=""
            aria-label={`Delete ${document.name}`}
            sx={{ '&:hover': { color: (theme) => theme.palette.custom.OV.sys.error } }}
            // The row selects on pointer and click, so the button keeps both to itself.
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              void handleDelete(document.legalDocumentId);
            }}
          >
            <DynamicIcon name="trash-2" library="lucide" sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      ) : undefined,
    };
  });

  return (
    <FormProvider {...surface.formMethods}>
      <LegalDocumentFields
        versionOptions={versionOptions}
        selectedVersionId={surface.selectedDocumentId}
        isNewVersion={surface.selectedDocumentId === NEW_DOCUMENT}
        onVersionChange={(documentId) => void handleSelect(documentId)}
        onNewVersion={() => void handleNewVersion()}
        sharingNote={
          savedDocument
            ? describeVersionSharing(savedDocument, selectedSegmentId, segmentName)
            : undefined
        }
        domainWarning={
          conflict ? describeSharedDomainConflict(conflict, segmentName) : undefined
        }
      />
    </FormProvider>
  );
};
